import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { Button } from "@shared/ui/primitives/Button";
import { toast } from "@shared/ui/feedback/Toast";
import { pageCopy } from "@shared/lib/pageCopy";
import { fetchMyResponses, fetchResidentSurvey, submitSurveyResponse } from "./api";
import { formTypeLabel, getSurveyRouteContext } from "./routeContext";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * Survey detail — intro screen for an available survey. "Start" hands
 * off to the server-driven flow renderer (the onboarding step engine,
 * which the Angular portal also reuses for survey fill-in).
 */
export function SurveyDetail(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const copy = pageCopy().surveys;
  const route = getSurveyRouteContext(location.pathname);

  const query = useQuery({
    queryKey: ["survey", id],
    queryFn: () => fetchResidentSurvey(id),
    enabled: !!id,
  });
  const responsesQuery = useQuery({
    queryKey: ["surveys-my"],
    queryFn: fetchMyResponses,
  });

  const myResponses = (responsesQuery.data ?? []).filter(
    (response) => response.survey_template_id === id,
  );
  const existingDraft = myResponses.find((response) => !response.submitted_at);
  // Submitted responses (newest first), so a member who has already completed the
  // form can view what they submitted — including past submissions on multi-response forms.
  const submittedResponses = myResponses
    .filter((response) => !!response.submitted_at)
    .sort(
      (a, b) =>
        new Date(b.submitted_at ?? b.created_at).getTime() -
        new Date(a.submitted_at ?? a.created_at).getTime(),
    );
  const latestSubmitted = submittedResponses[0];

  const startMutation = useMutation({
    mutationFn: () => {
      if (!query.data) throw new Error("Survey is not loaded yet.");
      if (existingDraft) return Promise.resolve(existingDraft);
      return submitSurveyResponse({
        survey_template_id: query.data.survey_template_id,
      });
    },
    onSuccess: (response) => {
      const params = new URLSearchParams({
        instance: response.flow_instance_id,
        context: "survey",
        kind: route.isEformRoute ? "eform" : "survey",
        title: query.data?.name ?? (route.isEformRoute ? "eForm" : "Survey"),
        backTo: route.listRoute,
        // On completion, land on the member's submission results.
        resultsTo: `/app/surveys/${response.survey_template_id}/response/${response.survey_instance_id}`,
      });
      navigate(`/survey/run?${params.toString()}`);
    },
    onError: (error) => {
      toast.danger(
        "Couldn't start survey",
        error instanceof Error ? error.message : undefined,
      );
    },
  });

  const survey = query.data;
  const formLabel = formTypeLabel(survey?.flow_type, route.isEformRoute);
  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={survey?.name ?? copy.title}
      subtitle={
        survey ? detailSubtitle(survey.deadline_at, formLabel) : copy.subtitle
      }
      backTo={route.listRoute}
      backLabel={route.detailBackLabel}
      backBehavior="parent"
    />
  );

  if (query.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState onRetry={() => query.refetch()} />
        </div>
      </div>
    );
  }

  const surveyData = query.data;
  const resolvedFormLabel = formTypeLabel(surveyData.flow_type, route.isEformRoute);
  const isClosed = surveyData.deadline_at
    ? new Date(surveyData.deadline_at).getTime() < Date.now()
    : false;
  // Defaults to true when unset, matching the server-side one-response-per-member default.
  const onePerMember = surveyData.response_policy?.one_per_member !== false;
  // A one-per-member form that's already been submitted can no longer be started again.
  const lockedAfterSubmission = onePerMember && !!latestSubmitted && !existingDraft;
  const hasMultipleSubmissions = submittedResponses.length > 1;
  const viewResponse = () => {
    if (!latestSubmitted) return;
    if (hasMultipleSubmissions) {
      // Multiple submissions → show the picker list.
      navigate(`/app/surveys/${surveyData.survey_template_id}/responses`);
      return;
    }
    // Single submission opens directly; back returns to this detail page.
    navigate(
      `/app/surveys/${surveyData.survey_template_id}/response/${latestSubmitted.survey_instance_id}`,
      {
        state: {
          backTo: location.pathname,
          backLabel: `Back to ${resolvedFormLabel}`,
        },
      },
    );
  };

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={surveyData.name}
        subtitle={detailSubtitle(surveyData.deadline_at, resolvedFormLabel)}
        backTo={route.listRoute}
        backLabel={route.detailBackLabel}
        backBehavior="parent"
      />

      <div className="flex flex-col gap-4 px-5 pt-5 pb-safe-or-6">
        {surveyData.display_context?.intro_message?.html?.trim() ? (
          <Card noGrain className="p-5">
            <div
              className="onboarding-content text-left text-body text-ink-secondary [&_h1]:font-display [&_h1]:text-display-lg [&_h1]:text-ink [&_h2]:font-display [&_h2]:text-headline [&_h2]:text-ink [&_h3]:font-display [&_h3]:text-headline [&_h3]:text-ink [&_p]:mt-1.5 [&_a]:text-primary-700 [&_ul]:list-disc [&_ul]:pl-5"
              // Content is authored in the tenant's survey template (trusted admin input).
              dangerouslySetInnerHTML={{ __html: surveyData.display_context.intro_message.html }}
            />
          </Card>
        ) : null}

        {surveyData.deadline_at ? (
          <Card noGrain className="p-4">
            <p className="text-caption uppercase tracking-[0.14em] text-ink-muted">
              Closing date
            </p>
            <p className="mt-1 font-display text-headline text-ink">
              {DATE_FMT.format(new Date(surveyData.deadline_at))}
            </p>
          </Card>
        ) : null}

        {latestSubmitted ? (
          <Button
            width="full"
            variant="secondary"
            disabled={responsesQuery.isLoading}
            onClick={viewResponse}
          >
            {hasMultipleSubmissions
              ? `View your responses (${submittedResponses.length})`
              : "View your response"}
          </Button>
        ) : null}

        {lockedAfterSubmission ? null : (
          <Button
            width="full"
            disabled={isClosed || responsesQuery.isLoading}
            loading={startMutation.isPending}
            onClick={() => startMutation.mutate()}
          >
            {isClosed
              ? `${resolvedFormLabel} Closed`
              : existingDraft
                ? `Continue ${resolvedFormLabel}`
                : latestSubmitted
                  ? `Submit another response`
                  : `Start ${resolvedFormLabel}`}
          </Button>
        )}
      </div>
    </div>
  );
}

function detailSubtitle(
  deadlineAt: string | null | undefined,
  formLabel: string,
): string {
  if (!deadlineAt) return `${formLabel} ready to complete`;
  return `${formLabel} closes ${DATE_FMT.format(new Date(deadlineAt))}`;
}
